/**
 * Credential helper script renderer — generates a POSIX `sh` script that git
 * invokes via `credential.<urlPrefix>.helper = !sh <path>` to fetch credentials
 * for a Tier-2 git mount.
 *
 * The script implements the wake-mid-401 hardening described in
 * `_devlog/specs/2026-05-07-unified-credential-mediation.md` § Step 9a:
 *
 *   1. On `op = get`, stat the credentials file and compute its age (mtime).
 *   2. If the file is empty/missing OR age > triggerAgeSec, touch the
 *      refresh-flag file. The runner watches this path via `fs.watch` and mints
 *      a fresh access token + rewrites the credentials file when it sees the
 *      change. Emptiness is its own trigger because a freshly truncated file
 *      has a *recent* mtime, so the age check alone can never heal it.
 *   3. Poll the credentials file in 200ms increments until its mtime advances,
 *      or it becomes non-empty (when it started empty), or waitTimeoutMs
 *      elapses.
 *   4. Translate the credentials file (`scheme://user:pass@host <tag>` lines —
 *      git's `store --file=` format) into the helper-output format git
 *      expects (`username=<value>\npassword=<value>\n`). Filters lines by the
 *      mount's url-prefix tag so multi-mount workspaces stay isolated.
 *
 * Two properties beyond the original Step 9a design:
 *
 *   - **The dance is bounded.** A refresh that keeps failing would otherwise
 *     make every single git network op pay a fresh `waitTimeoutMs` stall
 *     forever. The script stamps `refreshAttemptPath` after a dance that
 *     changed nothing and refuses to dance again within `refreshCooldownSec`.
 *   - **No credential is reported, not hidden.** Exiting 0 with no output makes
 *     git ask the next helper and then the terminal, so the operator sees
 *     `fatal: could not read Username for 'https://github.com'` — an error that
 *     names neither the mount nor the real cause. The script instead writes an
 *     attributable `skaile:` line to stderr and emits `quit=1`, which is the
 *     one mechanism that actually stops git's helper chain (a non-zero exit
 *     does not: git falls through to the username prompt anyway).
 *
 * When `unavailableReason` is set the mount is known not to be connected, and
 * the script skips the refresh dance entirely: the runner's refresh dispatcher
 * only iterates *connected* git connectors, so nothing on the runner side could
 * service the request — firing one would be a guaranteed-useless stall.
 *
 * **Cross-platform**: targets POSIX `sh` (Linux + macOS). Avoids bashisms so
 * `dash`, `ash`, and `busybox sh` all work. The two GNU/BSD divergence points
 * are handled inline:
 *
 *   - `stat -c %Y <file>` (GNU/Linux) vs `stat -f %m <file>` (BSD/macOS) —
 *     try GNU first, fall back to BSD, default to 0.
 *   - `sleep 0.2` is non-portable — use `usleep 200000` when present, else
 *     fall back to `sleep 1`.
 *
 * The credentials file format is unchanged (Option B in the Step 9a plan):
 * `scheme://user:pass@host <tag>` per mount, written by `writeMountBlock`.
 * The helper script does the translation to `username=<>\npassword=<>\n`
 * inline so we don't have to change the on-disk format and break Step 1A
 * back-compat.
 *
 * Spec: `_devlog/specs/2026-05-07-unified-credential-mediation.md`.
 */

import { dirname, join } from "node:path";

/**
 * Quote an arbitrary string as a single `sh` word. Values reaching the rendered
 * script include backend-supplied error text, so verbatim interpolation into a
 * single-quoted literal is a command-injection surface, not a cosmetic concern.
 */
function shQuote(value: string): string {
  return `'${value.replaceAll("'", "'\\''")}'`;
}

/** Collapse newlines so a multi-line value stays one attributable stderr line. */
function singleLine(value: string): string {
  return value.replace(/[\r\n]+/g, " ").trim();
}

/**
 * Recover the mount id from its credential tag (`# skaile-mount: <id>`) so the
 * "no credential" message can name the mount. Falls back to the raw tag for a
 * caller that passes some other tag shape.
 */
function mountIdFromTag(tag: string): string {
  const match = tag.match(/^#\s*skaile-mount:\s*(.+)$/);
  return (match?.[1] ?? tag).trim();
}

/** Filename-safe form of the credential tag, for the default attempt-stamp path. */
function stampSlug(tag: string): string {
  return (
    mountIdFromTag(tag)
      .replace(/[^A-Za-z0-9._-]+/g, "-")
      .replace(/^-+|-+$/g, "") || "mount"
  );
}

/** Required arguments for {@link renderCredentialHelperScript}. */
export interface RenderHelperOpts {
  /** Absolute path to the per-mount credentials file. */
  credentialsPath: string;
  /**
   * Absolute path to the workspace-level refresh-request flag file. The
   * runner watches this file via `fs.watch` and triggers a token refresh on
   * `change`.
   */
  refreshFlagPath: string;
  /**
   * Tag emitted by `writeMountBlock` on the credential line for this mount
   * (e.g. `# skaile-mount: workspace`). The helper greps for lines ending in
   * this tag so concurrent mounts in the same credentials file stay isolated.
   */
  credentialTag: string;
  /**
   * Touch the refresh-flag if the credentials file is older than this (in
   * seconds). Default: 300 (5 minutes — comfortably shorter than the typical
   * 1-hour Anthropic OAuth lifetime, well clear of the 5-minute pre-expiry
   * refresh window scheduled by the driver).
   */
  triggerAgeSec?: number;
  /**
   * Maximum time to wait for the credentials file mtime to advance after
   * touching the refresh-flag (in milliseconds). Default: 5000 (5 seconds —
   * long enough for the runner's debounced watcher + token mediator round-
   * trip; short enough that a stuck backend doesn't block git for minutes).
   */
  waitTimeoutMs?: number;
  /**
   * Set when the mount is known **not** to be connected — the connector failed
   * at connect time, so no credential will ever appear. The script then skips
   * the refresh dance and immediately tells git to stop, quoting this reason.
   *
   * The refresh dispatcher only iterates connected git connectors, so a failed
   * connector has nothing on the runner side that could service a refresh
   * request; firing one would stall for `waitTimeoutMs` and change nothing.
   */
  unavailableReason?: string;
  /**
   * Minimum gap, in seconds, between two refresh dances for this mount.
   * Default: 30. Bounds the cost of a refresh that legitimately keeps failing.
   *
   * A throttled invocation skips only the *wait*, never the read: the
   * credential lookup below runs unconditionally, so a token that landed before
   * the invocation is still served. The window it gives up is narrow — a
   * mediator round-trip that finishes after `waitTimeoutMs` but before the
   * cooldown expires — and the trade is deliberate: a bounded failure carrying
   * an accurate reason beats an unbounded stall on every git operation.
   */
  refreshCooldownSec?: number;
  /**
   * Absolute path to the per-mount stamp file the script writes itself after a
   * dance that produced nothing. Defaults to a sibling of the credentials file
   * derived from the tag; callers should pass it explicitly so the name is
   * predictable. Only this script reads or writes it — the runner never does.
   */
  refreshAttemptPath?: string;
}

/**
 * Render the credential-helper script body. The returned string should be
 * written to disk with mode `0755` (owner rwx, group/other rx) so git can
 * execute it.
 *
 * `git` invokes credential helpers with one of three operations as `$1`:
 *   - `get`   — read credentials. We respond with the helper-output format.
 *   - `store` — git wants us to remember a credential. We're a one-way
 *               read-only helper; ignore.
 *   - `erase` — git wants us to forget a credential. Same — ignore.
 *
 * Only `get` triggers the refresh-flag dance. Other ops exit silently with 0
 * so git falls back to the next helper in the chain (or its own prompt).
 *
 * The script also drains stdin on `get` because git always pipes the request
 * envelope (host, protocol, path, etc.) into the helper's stdin. Leaving it
 * unread can wedge git's helper FD.
 */
export function renderCredentialHelperScript(opts: RenderHelperOpts): string {
  const triggerAgeSec = opts.triggerAgeSec ?? 300;
  const waitTimeoutMs = opts.waitTimeoutMs ?? 5_000;
  // Round timeout up to whole seconds for the `sleep 1` fallback path.
  const waitTimeoutSec = Math.max(1, Math.ceil(waitTimeoutMs / 1_000));
  const cooldownSec = Math.max(0, Math.trunc(opts.refreshCooldownSec ?? 30));

  const tag = opts.credentialTag;
  const unavailable = opts.unavailableReason != null && opts.unavailableReason.trim().length > 0;
  const attemptPath =
    opts.refreshAttemptPath ??
    join(dirname(opts.credentialsPath), `refresh-attempt-${stampSlug(tag)}`);

  // The message git's transcript will carry when we have nothing to serve.
  // Prefixed `skaile:` so it is attributable among git's own diagnostics.
  const message = unavailable
    ? `skaile: mount not connected: ${singleLine(opts.unavailableReason ?? "")}`
    : `skaile: no credential available for git mount ${mountIdFromTag(tag)} — the workspace mount is not authenticated.`;

  const readsStillWorkNote =
    "skaile: git fetch/ls-remote can still succeed on a public remote; only writes need this credential.";

  return `#!/bin/sh
# Generated by skaile-runner -- do not edit.
# Tier-2 git credential helper: serves the latest cached token and triggers a
# wake-mid-401 refresh when the credentials file is empty or older than ${triggerAgeSec}s.
# Spec: _devlog/specs/2026-05-07-unified-credential-mediation.md

set -u

op="\${1:-}"
# 'store' and 'erase' are no-ops -- this is a read-only helper. Other ops
# (none today) also bail silently so we don't break future git versions.
[ "$op" = "get" ] || exit 0

CRED_FILE=${shQuote(opts.credentialsPath)}
FLAG_FILE=${shQuote(opts.refreshFlagPath)}
TAG=${shQuote(tag)}
ATTEMPT_FILE=${shQuote(attemptPath)}
MSG=${shQuote(message)}
UNAVAILABLE=${unavailable ? 1 : 0}

# Drain stdin: git always pipes the request envelope. Leaving it unread can
# wedge git's helper FD. We don't actually need any of it -- the credentials
# file already keys by URL prefix via the helper's gitconfig block.
cat > /dev/null 2>&1 || true

# 'quit=1' is what actually stops git's helper chain. Exiting 0 with no output
# sends git to the next helper and then the terminal prompt ("could not read
# Username"); exiting non-zero does the same. Only quit=1 makes git report our
# reason as the terminal failure.
if [ "$UNAVAILABLE" -eq 1 ]; then
  printf '%s\\n' "$MSG" >&2
  printf '%s\\n' ${shQuote(readsStillWorkNote)} >&2
  printf 'quit=1\\n'
  exit 0
fi

# Cross-platform mtime in epoch seconds: GNU stat -c first (Linux), then BSD
# stat -f (macOS), then 0 if neither is present (in which case we skip the
# refresh-flag dance entirely and just serve what we have).
get_mtime() {
  stat -c %Y "$1" 2>/dev/null || stat -f %m "$1" 2>/dev/null || echo 0
}

now=$(date +%s 2>/dev/null || echo 0)
mtime=$(get_mtime "$CRED_FILE")
age=$((now - mtime))

# Emptiness is its own trigger: a truncated file has a recent mtime, so the age
# check alone would leave it empty forever.
started_empty=0
[ -s "$CRED_FILE" ] || started_empty=1

if [ "$started_empty" -eq 1 ] || [ "$age" -gt ${triggerAgeSec} ]; then
  # Bound the dance. A refresh that keeps failing would otherwise cost every
  # git network op a fresh timeout, forever. The stamp below is written only
  # when a dance changed nothing, so a working refresh is never throttled.
  last_attempt=$(get_mtime "$ATTEMPT_FILE")
  if [ $((now - last_attempt)) -ge ${cooldownSec} ]; then
    # Touch the refresh-flag (atomic open+close emits 'change' to fs.watch).
    # The subshell is load-bearing: ':' is a POSIX *special built-in*, so a
    # redirection error on it terminates a non-interactive dash/ash outright --
    # '|| true' never runs, and the helper would die before reaching the
    # quit=1 block below. Run the redirection in a child instead.
    ( : > "$FLAG_FILE" ) 2>/dev/null || true

    # Poll for the credentials file mtime to advance. usleep gives sub-second
    # resolution where available; sleep 1 is the portable fallback. Resolve which
    # once, up front, rather than probing PATH on every iteration.
    if command -v usleep >/dev/null 2>&1; then
      have_usleep=1
    else
      have_usleep=0
    fi
    refreshed=0
    end=$((now + ${waitTimeoutSec}))
    while :; do
      cur=$(date +%s 2>/dev/null || echo 0)
      if [ "$cur" -ge "$end" ]; then
        break
      fi
      new_mtime=$(get_mtime "$CRED_FILE")
      if [ "$new_mtime" -gt "$mtime" ]; then
        refreshed=1
        break
      fi
      # A same-second rewrite doesn't advance the second-resolution mtime, so an
      # empty file that gained content must break on content, not on mtime.
      if [ "$started_empty" -eq 1 ] && [ -s "$CRED_FILE" ]; then
        refreshed=1
        break
      fi
      if [ "$have_usleep" -eq 1 ]; then
        usleep 200000
      else
        sleep 1
      fi
    done
    if [ "$refreshed" -eq 0 ]; then
      # Subshell for the same reason as the flag write above. Here it also
      # matters in the ordinary case: the stamp lives in a runner-owned
      # directory, so under uid separation the agent can only truncate a file
      # the runner pre-created. A missing pre-create must cost the cooldown,
      # never the quit=1 report.
      ( : > "$ATTEMPT_FILE" ) 2>/dev/null || true
    fi
  fi
fi

# Find the credential line for THIS mount (matched by the trailing tag) and
# translate from git's 'store --file=' format ('scheme://user:pass@host <tag>')
# into the helper-output format ('username=<>\\npassword=<>\\n'). If there's
# more than one matching line (shouldn't happen but defence-in-depth), we
# take the last one (most recently written).
line=$(grep -F " $TAG" "$CRED_FILE" 2>/dev/null | tail -n 1)
if [ -z "$line" ]; then
  # No credential to serve. Terminate the chain rather than exiting 0, which
  # would hand git back to its username prompt -- the misleading error this
  # helper exists to replace. The same fetch-succeeds/push-fails confusion
  # applies here as on the known-dead path, so say so either way.
  printf '%s\\n' "$MSG" >&2
  printf '%s\\n' ${shQuote(readsStillWorkNote)} >&2
  printf 'quit=1\\n'
  exit 0
fi

# Strip the trailing ' <TAG>' from the URL.
url=\${line% *}
# Extract user:pass between scheme:// and @host. The token (pass) was
# percent-encoded at write time (buildCredentialLine), so any literal '@' in it
# stays encoded here -- the rightmost '@' is unambiguously the host delimiter.
# scheme://user:pass@host  -> user:pass
userinfo=\${url#*://}
userinfo=\${userinfo%@*}
user=\${userinfo%%:*}
pass=\${userinfo#*:}

# Percent-decode the token (encodeURIComponent inverse). '%XX' -> byte; no '+'
# handling needed since encodeURIComponent emits '%20' for spaces.
pass=$(printf '%b' "$(printf '%s' "$pass" | sed 's/%/\\\\x/g')")

# git wants line-oriented key=value pairs followed by a blank line.
printf 'username=%s\\n' "$user"
printf 'password=%s\\n' "$pass"
printf '\\n'
`;
}
